import { z } from 'zod';
import type { BotSettingsPatch, CompanySettings } from '../schemas/companySettings';
import {
  BOT_PLANS,
  type BotConversationState,
  type BotDecision,
  type BotMessage,
  type BotPlan,
  type BotUnderstanding,
  type LlmUsage,
} from './types';

export interface BotSettingsDto {
  companyId: string;
  bot: CompanySettings['bot'];
  businessHours: CompanySettings['businessHours'];
  timezone: string;
}

export const updateBotSettingsSchema = z.object({
  companyId: z.string().uuid(),
  bot: z.record(z.string(), z.unknown()),
});
export type UpdateBotSettingsInput = z.infer<typeof updateBotSettingsSchema>;
export type UpdateBotSettingsPayload = { companyId: string; bot: BotSettingsPatch };

export const createBotSessionSchema = z.object({
  companyId: z.string().uuid(),
  projectId: z.string().uuid(),
  plan: z.enum(BOT_PLANS).optional(),
});
export type CreateBotSessionInput = z.infer<typeof createBotSessionSchema>;

export const botSessionMessageSchema = z.object({
  text: z.string().trim().min(1).max(1000),
});

export interface BotModelInfo {
  /** "anthropic:claude-sonnet-5" configurado (null = reglas y plantillas). */
  requested: string | null;
  /** Proveedor y modelo que se usarán de verdad. */
  provider: string;
  model: string;
  /** El proveedor pedido no tiene credenciales: se usa el simulado. */
  fallbackToFake: boolean;
}

export interface BotRunDto {
  id: string;
  createdAt: string;
  inputText: string | null;
  understanding: BotUnderstanding | null;
  decision: BotDecision | null;
  replyText: string | null;
  verified: boolean;
  issues: string[];
  usage: Partial<Record<'understand' | 'reply', LlmUsage>>;
  costUsd: number;
  latencyMs: number;
  models: { understand: BotModelInfo | null; reply: BotModelInfo | null };
  status: 'OK' | 'OMITIDO' | 'ERROR';
  error: string | null;
}

export interface BotSessionDto {
  id: string;
  companyId: string;
  projectId: string;
  projectName: string;
  plan: BotPlan;
  state: BotConversationState;
  messages: BotMessage[];
  runs: BotRunDto[];
  createdAt: string;
}

export interface BotTurnResponse {
  session: BotSessionDto;
  /** Último turno (para mostrar la traza al instante). */
  run: BotRunDto;
}

export interface BotUsageDto {
  companyId: string;
  month: string;
  budgetUsd: number;
  real: { runs: number; costUsd: number; input: number; cachedInput: number; output: number };
  simulated: { runs: number; costUsd: number };
  byModel: Array<{ model: string; runs: number; costUsd: number }>;
  byDay: Array<{ day: string; runs: number; costUsd: number }>;
}

export interface BotModelsDto {
  providers: Array<{ id: string; ready: boolean }>;
  plans: Record<BotPlan, { understand: BotModelInfo; reply: BotModelInfo }>;
  /** Modelo abierto del servidor compatible con OpenAI (LLM_COMPAT_MODEL), para la comparación. */
  compatModel: string;
}

// ---------------------------------------------------------------- 8.5b: el bot conversa por WhatsApp

/** Respuesta simulada del cliente (solo con número en modo SIMULADO): entra como mensaje y el bot contesta. */
export const simulateInboundSchema = z.object({
  text: z.string().trim().min(1).max(1000),
});
export type SimulateInboundInput = z.infer<typeof simulateInboundSchema>;

/** Turnos reales del bot sobre un lead (traza "por qué respondió así" para quien gestiona). */
export interface BotLeadRunDto extends BotRunDto {
  /** Mensaje de WhatsApp que el bot envió en ese turno (null si no escribió). */
  messageId: string | null;
  trigger: 'SIMULADOR' | 'ENTRANTE' | 'PROGRAMADO';
}

export interface BotLeadRunsDto {
  leadId: string;
  runs: BotLeadRunDto[];
}

// ---------------------------------------------------------------- 8.5c: batería de escenarios y comparación de modelos

export const BOT_EVALUATION_STATUSES = ['PENDIENTE', 'EN_PROCESO', 'OK', 'ERROR'] as const;
export type BotEvaluationStatus = (typeof BOT_EVALUATION_STATUSES)[number];
export const BOT_EVALUATION_STATUS_LABELS: Record<BotEvaluationStatus, string> = {
  PENDIENTE: 'En cola',
  EN_PROCESO: 'Corriendo',
  OK: 'Terminada',
  ERROR: 'Con error',
};

const modelRef = z
  .string()
  .trim()
  .max(120)
  .regex(
    /^[a-z0-9_-]+:[A-Za-z0-9._/-]+$/,
    'Formato proveedor:modelo (p. ej. anthropic:claude-haiku-4-5)',
  )
  .nullable()
  .optional();

/** Una variante a comparar: un plan y, opcionalmente, modelos distintos a los del plan. */
export const botVariantSchema = z.object({
  key: z
    .string()
    .trim()
    .min(1)
    .max(40)
    .regex(/^[a-z0-9-]+$/),
  label: z.string().trim().min(1).max(60),
  plan: z.enum(BOT_PLANS),
  models: z.object({ understand: modelRef, reply: modelRef }).optional(),
});
export type BotVariant = z.infer<typeof botVariantSchema>;

export const createBotEvaluationSchema = z.object({
  companyId: z.string().uuid(),
  projectId: z.string().uuid(),
  variants: z.array(botVariantSchema).min(1).max(6),
  /** Subconjunto de escenarios (ids); vacío = toda la batería. */
  scenarioIds: z.array(z.string().max(60)).max(100).optional(),
});
export type CreateBotEvaluationInput = z.infer<typeof createBotEvaluationSchema>;

export interface BotEvaluationTurnDto {
  customer: string | null;
  intent: string | null;
  temperature: string | null;
  action: string;
  priority: boolean;
  reply: string | null;
  verified: boolean;
  issues: string[];
  costUsd: number;
  latencyMs: number;
}

export interface BotEvaluationScenarioDto {
  id: string;
  title: string;
  group: string;
  passed: number;
  total: number;
  failures: string[];
  costUsd: number;
  latencyMs: number;
  turns: BotEvaluationTurnDto[];
}

export interface BotEvaluationVariantSummary {
  key: string;
  label: string;
  plan: BotPlan;
  models: { understand: BotModelInfo | null; reply: BotModelInfo | null };
  scenarios: number;
  /** Escenarios con todas sus comprobaciones en verde. */
  passedScenarios: number;
  checksPassed: number;
  checksTotal: number;
  costUsd: number;
  costPerConversation: number;
  avgLatencyMs: number;
  /** Respuestas del modelo descartadas por la verificación (se envió la plantilla). */
  verificationFailures: number;
  /** Algún modelo pedido no tenía clave y corrió simulado. */
  fallbackToFake: boolean;
  errors: number;
}

export interface BotEvaluationVariantResult extends BotEvaluationVariantSummary {
  scenarios_detail: BotEvaluationScenarioDto[];
}

export interface BotEvaluationDto {
  id: string;
  companyId: string;
  projectId: string;
  projectName: string;
  status: BotEvaluationStatus;
  progress: number;
  total: number;
  error: string | null;
  createdAt: string;
  finishedAt: string | null;
  createdByName: string | null;
  variants: BotVariant[];
  /** Resumen por variante (disponible al terminar). */
  summaries: BotEvaluationVariantSummary[] | null;
  /** Detalle por variante y escenario (solo en el detalle de una evaluación). */
  results: BotEvaluationVariantResult[] | null;
}

export interface BotScenarioDto {
  id: string;
  title: string;
  group: string;
  messages: string[];
}
